/**
 * Field Registry - Lookup configurations for dropdown fields
 *
 * This module provides:
 * - lookupRegistry: API configurations for dropdown fields
 * - Lookup utility functions
 *
 * The column `id` is always used as the data field name.
 *
 * NOTE: Uses lazy initialization to avoid import-time errors in tests
 * where API modules may not be properly initialized.
 */

import type { Response } from 'types';
import type { ComboBoxApi } from 'components/inputs/types';

// Import API modules at top level (ES modules)
// These are only used when the registry is actually accessed
import { apiBrands } from 'api/brands';
import { apiBrandsDelogue } from 'api/brands.delogueApi';
import { USE_FEDERATED_AUTH } from 'config';
import { apiGroups } from 'api/groups';
import { apiSeasons } from 'api/seasons';
import { apiSuppliers } from 'api/suppliers';
import { apiUsers } from 'api/users';
import { apiAdminUnits } from 'api/adminUnits';
import { apiCategories } from 'api/categories';
import { apiStyleCategories } from 'api/styleCategories';
// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Standard transform for { id, name } API responses.
 * Handles both full response { data: [...] } and unwrapped array (from useGet/adapter).
 */
const transformIdName = (response: unknown) => {
  const data = Array.isArray(response)
    ? response
    : (response as Response<{ id: number; name: string }[]>)?.data;
  if (!data || !Array.isArray(data)) return [];
  return data.map(item => ({ id: item.id, label: item.name }));
};

/**
 * Transform for AdminUnit API responses - includes isFraction flag
 */
const transformAdminUnit = (response: unknown) => {
  const data = (response as Response<{ id: number; name: string; isFraction: boolean }[]>).data;
  return data.map(item => ({ id: item.id, label: item.name, isFraction: item.isFraction }));
};

/**
 * Transform for direct array API responses (no .data wrapper)
 * Used for legacy endpoints like /itemCategory
 * Handles both camelCase and PascalCase property names
 */
const transformDirectArray = (response: unknown) => {
  const data = response as { id?: number; Id?: number; name?: string; Name?: string }[];
  return data.map(item => ({
    id: item.id ?? item.Id ?? 0,
    label: item.name ?? item.Name ?? '',
  }));
};

/** Supplier org users — preserve brand access for bulk-edit filtering (camelCase API). */
const transformSupplierContactUsers = (response: unknown) => {
  const data = Array.isArray(response)
    ? response
    : response != null &&
        typeof response === 'object' &&
        'data' in response &&
        Array.isArray(Reflect.get(response, 'data'))
      ? Reflect.get(response, 'data')
      : undefined;
  if (!data || !Array.isArray(data)) return [];
  return data.map(
    (item: {
      id: string;
      name: string;
      accessToAllBrands?: boolean;
      assignedBrands?: Array<{ id: number; name?: string }>;
    }) => ({
      id: item.id,
      label: item.name,
      customProps: {
        accessToAllBrands: item.accessToAllBrands === true,
        assignedBrands: item.assignedBrands ?? [],
      },
    })
  );
};

// ============================================================================
// Lookup Registry - API configurations for dropdown fields
// ============================================================================

/**
 * Lookup Registry - Maps column IDs to their API configurations
 *
 * When a column has type='dd' and no explicit api property,
 * the system will use this registry to find the API config.
 *
 * To add a new lookup:
 * 1. Add the API endpoint to the appropriate api/*.ts file
 * 2. Add an entry here with the column ID as the key
 *
 * NOTE: Registry is lazily initialized to avoid creating objects at import time
 */
let lookupRegistry: Record<string, ComboBoxApi> | null = null;

/**
 * Initialize the lookup registry with API configurations
 * Called lazily on first access
 */
export const initializeRegistry = (): Record<string, ComboBoxApi> => {
  return {
    // Entity lookups — `search: true` passes combobox input as first `vars` slot (see ComboBox useGet).
    brand: {
      url: USE_FEDERATED_AUTH ? apiBrandsDelogue.get.search : apiBrands.get.search,
      key: USE_FEDERATED_AUTH ? 'brands-delogue' : 'brands',
      search: true,
      revalidate: true,
      responseTransform: transformIdName,
    },
    group: {
      url: apiGroups.get.search,
      key: 'groups',
      search: true,
      revalidate: true,
      responseTransform: transformIdName,
    },
    season: {
      url: apiSeasons.get.search,
      key: 'seasons',
      search: true,
      revalidate: true,
      responseTransform: transformIdName,
    },
    /** Item rows — `isItemSupplier=true` */
    supplier: {
      url: apiSuppliers.get.listItemSuppliersSearch,
      key: 'suppliers-item-search',
      search: true,
      revalidate: true,
      responseTransform: transformIdName,
    },
    /** Style rows — `isStyleSupplier=true` (column id `styleSupplier`, row field still `supplier`) */
    styleSupplier: {
      url: apiSuppliers.get.listStyleSuppliersSearch,
      key: 'suppliers-style-search',
      search: true,
      revalidate: true,
      responseTransform: transformIdName,
    },

    // User lookups (used for contact person fields)
    companyContactPerson: {
      url: apiUsers.get.getUsersForOrganization,
      key: 'companyContactPersons',
      search: true,
      vars: (context?: unknown) => {
        const ctx = context as {
          userData?: { loginContext?: { designerOrganizationId?: number } };
        };
        const organizationId = ctx?.userData?.loginContext?.designerOrganizationId || '';
        const fetchAssociatedDesigners = 'false';
        // ComboBox prepends parsed search as vars[0]; URL uses {0}=search, {2}=org, {3}=flag.
        return ['', String(organizationId), fetchAssociatedDesigners];
      },
      revalidate: true,
      responseTransform: transformIdName,
    },
    supplierContactPerson: {
      url: apiUsers.get.getUsersForOrganization,
      key: 'supplierContactPersons',
      search: true,
      vars: (context?: unknown) => {
        const ctx = context as {
          rowData?: { supplier?: { id?: number }; organization?: { id?: number } };
          formValues?: { supplier?: { id?: number }; styleSupplier?: { id?: number } };
          effectiveSupplierId?: number | string;
        };
        const fromRow = ctx?.rowData?.supplier?.id;
        const supplierFromForm =
          ctx?.formValues?.supplier ?? ctx?.formValues?.styleSupplier ?? undefined;
        const fromForm =
          supplierFromForm &&
          typeof supplierFromForm === 'object' &&
          supplierFromForm !== null &&
          'id' in supplierFromForm
            ? (supplierFromForm as { id: number }).id
            : undefined;
        const fromEffective =
          ctx?.effectiveSupplierId != null ? String(ctx.effectiveSupplierId) : '';
        const organizationId = fromRow ?? fromForm ?? fromEffective;
        if (organizationId === '') {
          return [];
        }
        const fetchAssociatedDesigners = 'false';
        return ['', String(organizationId), fetchAssociatedDesigners];
      },
      revalidate: true,
      responseTransform: transformSupplierContactUsers,
    },

    // Admin lookups
    unit: {
      url: apiAdminUnits.get.list,
      key: 'adminUnits',
      revalidate: true,
      responseTransform: transformAdminUnit,
    },
    adminUnit: {
      url: apiAdminUnits.get.list,
      key: 'adminUnits',
      revalidate: true,
      responseTransform: transformAdminUnit,
    },

    // Category lookups
    categories: {
      url: apiCategories.get.search,
      key: 'categories',
      search: true,
      revalidate: true,
      responseTransform: transformIdName,
    },
    styleCategories: {
      url: apiStyleCategories.get.search,
      key: 'styleCategories',
      search: true,
      revalidate: true,
      responseTransform: transformIdName,
    },
  };
};

/**
 * Get the lookup registry, initializing it if needed
 */
export const getRegistry = (): Record<string, ComboBoxApi> => {
  if (!lookupRegistry) {
    lookupRegistry = initializeRegistry();
  }
  return lookupRegistry;
};

/**
 * Get API config for a column ID
 * Returns undefined if no lookup is registered for this ID
 */
export const getLookupApi = (columnId: string): ComboBoxApi | undefined => {
  return getRegistry()[columnId];
};

/**
 * Reset the cached lookup registry (for testing purposes only)
 * @internal
 */
export const resetLookupRegistryForTesting = (): void => {
  lookupRegistry = null;
};
